/**
 * The authenticated application shell.
 *
 * Responsive by construction: a permanent sidebar from `lg` up, an off-canvas
 * drawer below it. The target user is often on a phone behind a counter, so
 * the mobile layout is the primary one, not a fallback.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Bike,
  Boxes,
  ChevronDown,
  ClipboardList,
  HandCoins,
  CreditCard,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Receipt,
  Route,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Tags,
  Timer,
  Truck,
  Upload,
  UserSquare2,
  Users,
  Wallet,
  Coins,
  TrendingUp,
  X,
  User as UserIcon,
  Landmark,
  PackagePlus,
  Pill,
  FileText,
  HeartPulse,
  ArrowLeftRight,
  Thermometer,
  CalendarClock,
  Siren,
  ScanLine,
} from 'lucide-react';
import { keepsCounterCash, type ModuleKey, type Permission } from '@cenox/shared';
import { useAuth } from '../lib/auth';
import { useWorkingLocation } from '../lib/locationContext';
import { useModules } from '../lib/modules';
import { initials } from '../lib/format';
import { Button, cn } from '../components/ui';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { BusinessSwitcher } from '../components/BusinessSwitcher';
import { SidebarScope } from '../components/GeoScope';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission: Permission;
  /** A second permission that also opens the screen, for one decided by either of two roles. */
  orPermission?: Permission;
  /**
   * The vertical this screen belongs to, when it belongs to one.
   *
   * Absent means every business gets it — the shared core of products, sales,
   * expenses and reports. Present means the business must actually have that
   * module, so a retail shop is never shown a rider's screen.
   */
  module?: ModuleKey;
  /**
   * A parent that opens and closes rather than navigating. Its own `to` is
   * only a key; each child is a screen. A parent shows when at least one child
   * is allowed, and collapses into that child when it is the only one — a
   * group of one is a click for nothing.
   */
  children?: NavItem[];
}

/** Whether this person's permissions open a menu item — either of its two, where it has two. */
export function navItemAllowed(item: NavItem, can: (permission: Permission) => boolean): boolean {
  return can(item.permission) || (item.orPermission !== undefined && can(item.orPermission));
}

/**
 * Narrow each group to the children this person may open.
 *
 * A group with none disappears, and a group with exactly one becomes that one
 * item — so somebody who can see Expenses but not Loans gets the plain
 * Expenses link they always had.
 */
export function resolveGroups(items: NavItem[], allowed: (item: NavItem) => boolean): NavItem[] {
  return items.flatMap((item) => {
    if (!item.children) return allowed(item) ? [item] : [];
    const children = item.children.filter(allowed);
    if (children.length === 0) return [];
    if (children.length === 1) return [children[0]!];
    return [{ ...item, children }];
  });
}

/** The earnings screens: delivery businesses only, read by whoever reads reports. */
/** One service date, per rider: in transit and delivered. Same audience as the earnings screens. */
const FINANCE_DAILY_RIDER_EARNINGS: NavItem = {
  to: '/finance/daily-rider-earnings',
  label: 'Daily rider earnings',
  icon: Wallet,
  permission: 'reports:read',
  module: 'delivery',
};
const FINANCE_RIDER_EARNINGS: NavItem = {
  to: '/finance/rider-earnings',
  label: 'Rider earnings',
  icon: Wallet,
  permission: 'reports:read',
  module: 'delivery',
};
const FINANCE_COMPANY_EARNINGS: NavItem = {
  to: '/finance/company-earnings',
  label: 'Company earnings',
  icon: TrendingUp,
  permission: 'reports:read',
  module: 'delivery',
};

/**
 * Where the money is: cash at each location and the bank. Any business.
 * A worker who only hands over cash (`cash:deposit`) gets it too, showing
 * just the cash they hold and what waits on them.
 */
const FINANCE_CASH_BANK: NavItem = {
  to: '/finance/accounts',
  label: 'Cash & bank',
  icon: Landmark,
  permission: 'reports:read',
  orPermission: 'cash:deposit',
};

/** Investors and their investments: vehicle partnerships, equity, profit sharing. Any business. */
const FINANCE_INVESTORS: NavItem = {
  to: '/finance/investors',
  label: 'Investors',
  icon: HandCoins,
  permission: 'investments:read',
};

/**
 * Finance for a business without deliveries — a shop, a pharmacy, a service.
 *
 * The cash screen ("Money & Stock", or "Finance" with no stock) becomes the
 * group's Overview, with the accounts and investors beside it, so there is
 * one Finance entry rather than three loose links. `between` holds whatever a
 * vertical adds (the pharmacy's own finance report). Each child keeps its own
 * permission; the group shows when any child does.
 */
function financeGroup(overview: NavItem | null, between: NavItem[] = []): NavItem {
  return {
    to: '/finance#group',
    label: 'Finance',
    icon: Coins,
    permission: 'reports:read',
    children: [
      ...(overview ? [{ ...overview, label: 'Overview' }] : []),
      ...between,
      FINANCE_CASH_BANK,
      FINANCE_INVESTORS,
    ],
  };
}

/**
 * The menu in the order this business works, and named for what it holds.
 *
 * Decided by MODULES, never by which business this is:
 *   - Delivery on: Deliveries and Logistics come straight after Dashboard —
 *     for a business that has them, they are the day's work.
 *   - Delivery on: Finance is a group of the rider and company earnings, the
 *     accounts and investors; without inventory the cash screen moves inside
 *     it as "Money & cash", with inventory it keeps its "Money & Stock" entry.
 *     With neither Sales & POS nor Inventory (a courier) there is no counter
 *     cash, so no "Money & cash" at all — Cash & bank holds its money.
 *   - Delivery off: Finance is a group with the cash screen as Overview, then
 *     Cash & bank and Investors.
 *
 * Children are added here unfiltered: run `resolveGroups` AFTER this, so each
 * child is shown only to someone with its permission.
 *
 * `active` is null when the module list could not be read, in which case the
 * menu is left as declared rather than guessed at.
 */
export function arrangeNav(items: NavItem[], active: Set<ModuleKey> | null): NavItem[] {
  if (!active) return items;
  const named = items.flatMap((item): NavItem[] => {
    if (item.to !== '/accountability') return [item];
    /*
      Finance, for a business with deliveries: what riders earned each day
      and what the company earned, beside the cash. One "Finance" entry, never
      two. Without inventory the cash screen moves inside it as "Money &
      cash" — there is no stock in it to name — and with inventory it keeps
      its own "Money & Stock" entry, with the earnings grouped after it.
    */
    if (active.has('delivery')) {
      const finance: NavItem = {
        to: '/finance#group',
        label: 'Finance',
        icon: Coins,
        permission: 'reports:read',
        children: [
          // A courier keeps no counter cash (keepsCounterCash): its money is on Cash & bank.
          ...(active.has('inventory') || !keepsCounterCash(active) ? [] : [{ ...item, label: 'Money & cash' }]),
          FINANCE_DAILY_RIDER_EARNINGS,
          // No "Rider settlement": it was removed (its records are kept).
          FINANCE_RIDER_EARNINGS,
          FINANCE_COMPANY_EARNINGS,
          FINANCE_CASH_BANK,
          FINANCE_INVESTORS,
        ],
      };
      return active.has('inventory') ? [item, finance] : [finance];
    }
    // Without deliveries: one Finance group, the cash screen as its Overview.
    return [financeGroup(item)];
  });
  if (!active.has('delivery')) return named;
  const isDelivery = (item: NavItem) => item.module === 'delivery';
  const [first, ...rest] = named;
  if (!first) return named;
  /*
    A delivery business deals with its customers before it reads its sales —
    the customer is who the job is for — so Customers moves up beside
    Locations: Dashboard → Deliveries → Logistics → Locations → Customers →
    Sales → Expenses → Staff → Reports → Finance → Assistant.

    "Before Sales" is a POSITION, not that item. A logistics business runs
    with `sales_pos` off and Remittance & Hire Purchase in that slot instead,
    so keying on `/sales` alone put Customers back at the bottom the moment
    Sales left the menu. Whichever of the two occupies the slot anchors it.
  */
  const others = rest.filter((item) => !isDelivery(item));
  const customers = others.find((item) => item.to === '/customers');
  const anchors = ['/sales', '/business/rider-finance'];
  const salesAt = others.findIndex((item) => anchors.includes(item.to));
  const reordered =
    customers && salesAt >= 0
      ? [
          ...others.slice(0, salesAt).filter((item) => item !== customers),
          customers,
          ...others.slice(salesAt).filter((item) => item !== customers),
        ]
      : others;
  return [first, ...rest.filter(isDelivery), ...reordered];
}

/*
  Expenses and Loans, one group: both are money leaving the business that
  somebody has to account for. Separate screens and separate ledgers — a
  loan is never an expense, and its principal never reaches the expense
  totals — but the same place to look. The same group in every menu, a
  pharmacy's included: no module decides it, only permissions.
*/
const EXPENSES_GROUP: NavItem = {
  to: '/expenses#group',
  label: 'Expenses',
  icon: Receipt,
  permission: 'expenses:read',
  orPermission: 'loans:read',
  children: [
    { to: '/expenses', label: 'Expenses', icon: Receipt, permission: 'expenses:read' },
    { to: '/loans', label: 'Loans', icon: HandCoins, permission: 'loans:read' },
  ],
};

export const NAV: NavItem[] = [
  // `/business`, not `/`. The root is CenoX Home now — a Dashboard link
  // pointing there would walk somebody out of the product they are working in.
  { to: '/business', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard:view' },
  // The till comes first after the dashboard: it is the screen a shop actually
  // spends its day on, and burying it under Sales would be a filing decision
  // rather than a usage one.
  // Shop, Products, Receiving and Stock count are Inventory: on for a shop,
  // off by default for a Delivery & Logistics business. See
  // BUSINESS_TYPE_MODULE_DEFAULTS — nothing here names a business.
  { to: '/shop', label: 'Shop', icon: ShoppingBag, permission: 'sales:create', module: 'inventory' },
  { to: '/catalogue', label: 'Products', icon: Boxes, permission: 'products:read', module: 'inventory' },
  // Above Stock count deliberately: goods arrive before they are counted, and
  // the menu reads in the order the day actually happens.
  { to: '/receiving', label: 'Receiving', icon: Truck, permission: 'receiving:read', module: 'inventory' },
  // Locations are where everything else happens, so they sit with the shop
  // rather than buried in Settings. Internally these are branches.
  // Gated on UPDATE, not read. `branches:read` is how anybody's app asks
  // "which locations are mine" — a worker needs it to reach their own counter —
  // so using it here put a Locations menu item in front of a courier. The page
  // is for managing locations and who works in them, which is the authority
  // its controls already require.
  { to: '/locations', label: 'Locations', icon: MapPin, permission: 'branches:update' },
  /*
    Sales and Remittance & Hire Purchase, in the same slot.

    Which one a business sees is decided by CAPABILITY, never by name or id:
    `sales_pos` on → Sales; `rider_finance` on → Remittance & Hire Purchase;
    both on → both, in this order; neither → neither. A Delivery & Logistics
    business starts with the second and not the first, because its money is
    riders settling weekly on bikes the company financed rather than a counter
    ringing up goods. See BUSINESS_TYPE_MODULE_DEFAULTS — a shop that also
    runs couriers gets both from one switch, and nothing here knows what any
    particular business is called.
  */
  { to: '/sales', label: 'Sales', icon: ShoppingCart, permission: 'sales:read', module: 'sales_pos' },
  {
    to: '/business/rider-finance',
    label: 'Remittance & Hire Purchase',
    icon: Wallet,
    // The lowest authority that has anything to do here. Collecting a week's
    // remittance is a counter job; `bikes:read` alone also opens the screen.
    permission: 'remittance:collect',
    module: 'rider_finance',
  },
  EXPENSES_GROUP,
  { to: '/customers', label: 'Customers', icon: Users, permission: 'customers:read' },
  { to: '/staff', label: 'Staff', icon: UserSquare2, permission: 'staff:read' },
  { to: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports:read' },
  // Named for the question it answers, not for the accounting concept.
  { to: '/accountability', label: 'Money & Stock', icon: ShieldCheck, permission: 'reports:read' },
  // Its own entry rather than buried inside the dashboard: counting is a task
  // somebody starts on their feet, not something they browse to.
  { to: '/stock-counts', label: 'Stock count', icon: ClipboardList, permission: 'stock:count', module: 'inventory' },
  // Logistics only. A shop has no deliveries, so it is not offered the screen.
  {
    to: '/deliveries',
    label: 'Deliveries',
    icon: Bike,
    permission: 'deliveries:read',
    module: 'delivery',
  },
  /*
    Prices, beside the book they govern.

    Delivery approvals used to sit here too. They left the menu on the owner's
    instruction; the screen, its route and every approval record are
    unchanged, at /business/deliveries/approvals.
  */
  {
    to: '/business/deliveries/prices',
    label: 'Delivery prices',
    icon: Tags,
    permission: 'delivery_pricing:manage',
    module: 'delivery',
  },
  /*
    Beside prices, and behind the same authority. Whoever decides what a job
    costs decides when it is late — not `deliveries:manage`, which a rider
    holds so they can update the run they just did.
  */
  {
    to: '/business/deliveries/deadlines',
    label: 'Delivery deadlines',
    icon: Timer,
    permission: 'delivery_pricing:manage',
    module: 'delivery',
  },
  /*
    The Logistics area, beside the delivery book rather than replacing it.

    `/deliveries` is where a dispatcher records a job; this is where an owner
    reads what the jobs came to. Two different tasks, and folding the second
    into the first would bury settlement behind a list of today's rides.
  */
  {
    to: '/business/logistics',
    label: 'Logistics',
    icon: Route,
    permission: 'reports:read',
    module: 'delivery',
  },
  { to: '/assistant', label: 'Assistant', icon: Sparkles, permission: 'ai:use' },
  { to: '/imports', label: 'Import', icon: Upload, permission: 'imports:manage' },
  { to: '/billing', label: 'Billing', icon: CreditCard, permission: 'settings:read' },
  { to: '/settings', label: 'Settings', icon: Settings, permission: 'settings:read' },
];

/**
 * The menu for a business running the Pharmacy module.
 *
 * Chosen by the MODULE, like everything else here: any business with Pharmacy
 * switched on gets it, and none without. Batches, expiry, counts, recalls and
 * storage live together under Medicines & Stock, because they are one job —
 * knowing what is on the shelf and whether it may be sold.
 */
export const PHARMACY_NAV: NavItem[] = [
  { to: '/pharmacy', label: 'Dashboard', icon: LayoutDashboard, permission: 'pharmacy:read', orPermission: 'dashboard:view' },
  { to: '/pharmacy/pos', label: 'POS', icon: ScanLine, permission: 'pharmacy:sell' },
  /*
    The ordinary till, for what is not a pharmacy item yet. It refuses any
    product whose pharmacy record tracks batches or is not OTC (see
    assertNotPharmacyStock), so it is no way round a prescription or the
    restricted register — only a way to sell what Pharmacy has not classified.
  */
  { to: '/shop', label: 'Shop till', icon: ShoppingBag, permission: 'sales:create', module: 'inventory' },
  { to: '/customers', label: 'Customers', icon: Users, permission: 'customers:read' },
  {
    to: '/pharmacy/stock#group',
    label: 'Medicines & Stock',
    icon: Pill,
    permission: 'pharmacy:read',
    orPermission: 'storage:log',
    children: [
      // Every product on file, medicine or not, classified or awaiting it.
      { to: '/pharmacy/stock', label: 'Medicines & products', icon: Pill, permission: 'pharmacy:read' },
      // Opening stock (pharmacy:stock) or a delivery (pharmacy:purchasing), typed in.
      { to: '/pharmacy/stock/entry', label: 'Add stock', icon: PackagePlus, permission: 'pharmacy:stock', orPermission: 'pharmacy:purchasing' },
      { to: '/pharmacy/stock/batches', label: 'Batches & expiry', icon: CalendarClock, permission: 'pharmacy:read' },
      { to: '/pharmacy/stock/adjustments', label: 'Adjustments & quarantine', icon: ShieldCheck, permission: 'pharmacy:read' },
      { to: '/pharmacy/stock/counts', label: 'Stock counts', icon: ClipboardList, permission: 'pharmacy:read' },
      { to: '/pharmacy/stock/recalls', label: 'Recalls', icon: Siren, permission: 'recalls:manage' },
      { to: '/pharmacy/stock/storage', label: 'Storage monitoring', icon: Thermometer, permission: 'storage:log', orPermission: 'pharmacy:read' },
    ],
  },
  { to: '/pharmacy/prescriptions', label: 'Prescriptions', icon: FileText, permission: 'prescriptions:read' },
  { to: '/pharmacy/patients', label: 'Patients', icon: HeartPulse, permission: 'patients:read' },
  { to: '/pharmacy/purchasing', label: 'Purchasing & Suppliers', icon: Truck, permission: 'pharmacy:purchasing' },
  { to: '/pharmacy/transfers', label: 'Transfers', icon: ArrowLeftRight, permission: 'pharmacy:transfers' },
  // Rent, power, wages and repairs; money lent and borrowed. Not stock: buying stock is Purchasing.
  EXPENSES_GROUP,
  // The core money screens, with the pharmacy's own finance report beside them.
  financeGroup(
    { to: '/accountability', label: 'Overview', icon: ShieldCheck, permission: 'reports:read' },
    [{ to: '/pharmacy/finance', label: 'Pharmacy finance', icon: Coins, permission: 'reports:read', orPermission: 'pharmacy:till' }],
  ),
  { to: '/pharmacy/reports', label: 'Reports', icon: BarChart3, permission: 'reports:read' },
  { to: '/staff', label: 'Staff', icon: UserSquare2, permission: 'staff:read' },
  {
    to: '/pharmacy/settings#group',
    label: 'Settings',
    icon: Settings,
    permission: 'settings:read',
    children: [
      { to: '/pharmacy/settings', label: 'Pharmacy settings', icon: Settings, permission: 'settings:read' },
      { to: '/pharmacy/settings/import', label: 'Import & migration', icon: Upload, permission: 'imports:manage', orPermission: 'pharmacy:read' },
      { to: '/locations', label: 'Locations', icon: MapPin, permission: 'branches:update' },
      { to: '/settings', label: 'Business settings', icon: Settings, permission: 'settings:read' },
      { to: '/billing', label: 'Billing', icon: CreditCard, permission: 'settings:read' },
    ],
  },
];

/**
 * The menu this person sees: arranged by the business's modules, THEN narrowed
 * to what they may open. Arranging first matters — the Finance groups are
 * built in `arrangeNav`, and narrowing before that would let their children
 * through unchecked. Visibility only: every screen and API checks again.
 */
export function navFor(active: Set<ModuleKey> | null, allowed: (item: NavItem) => boolean): NavItem[] {
  const moduleOn = (item: NavItem) => !item.module || !active || active.has(item.module);
  if (active?.has('pharmacy')) {
    const withModules = (items: NavItem[]): NavItem[] =>
      items.filter(moduleOn).map((i) => (i.children ? { ...i, children: withModules(i.children) } : i));
    return resolveGroups(withModules(PHARMACY_NAV), allowed);
  }
  return resolveGroups(arrangeNav(NAV.filter(moduleOn), active), allowed);
}

export function AppShell(): ReactNode {
  const { user, business, logout, can } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Navigating on mobile should dismiss the drawer, or the new page is hidden
  // behind it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const { active: modules, known: modulesKnown } = useModules();
  /**
   * Two questions, not one: may this person do it, and does this business have
   * it at all.
   *
   * The second is only asked when it can be answered. A worker has no
   * `settings:read`, so the module list is refused for them — and treating that
   * refusal as "no modules" hid Deliveries from the one role that exists to
   * record deliveries. Permissions are the gate; this is menu tidying, and
   * tidying must not remove somebody's only tool.
   */
  const moduleOn = (item: NavItem) => !item.module || !modulesKnown || modules.has(item.module);
  const allowed = (item: NavItem) => navItemAllowed(item, can) && moduleOn(item);
  const visibleNav = navFor(modulesKnown ? modules : null, allowed);

  return (
    <div className="min-h-dvh bg-slate-50">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50
                   focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
        >
          <Menu className="size-5" />
        </button>
        <span className="truncate font-semibold text-slate-900">
          {business?.businessName ?? 'CenoX Business'}
        </span>
      </header>

      {/* Backdrop for the mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-slate-200 bg-white',
          'transition-transform duration-200 lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
          <div className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
              CX
            </div>
            <span className="font-semibold text-slate-900">CenoX Business</span>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="border-b border-slate-200 p-3">
          <BusinessSwitcher />
          {/*
            Under the business name, not beside it, because it reads as what it
            is: this business, then the area inside it you are looking at —
            Country → State/Region → Location, shared by every screen.
          */}
          <SidebarScope />
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
          {visibleNav.map((item) =>
            item.children ? (
              <NavGroup key={item.to} item={item} pathname={location.pathname} />
            ) : (
              <NavEntry key={item.to} item={item} />
            ),
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-2.5 px-2.5 py-2">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
              {initials(user?.fullName ?? '?')}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{user?.fullName}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Link
            to="/account"
            className="mt-1 flex h-8 w-full items-center gap-1.5 rounded-lg px-3 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <UserIcon className="size-4" aria-hidden />
            Your account
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            icon={<LogOut className="size-4" />}
            onClick={() => void logout()}
          >
            Sign out
          </Button>
        </div>
      </aside>

      <main id="main" className="lg:pl-72">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <SubscriptionBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavEntry({ item, nested = false }: { item: NavItem; nested?: boolean }): ReactNode {
  return (
    <NavLink
      to={item.to}
      // A parent path must not light up for its children's screens.
      end={['/', '/pharmacy', '/pharmacy/stock', '/pharmacy/settings'].includes(item.to)}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          nested && 'pl-9',
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
        )
      }
    >
      {!nested && <item.icon className="size-4.5 shrink-0" aria-hidden />}
      {item.label}
    </NavLink>
  );
}

/**
 * A parent that opens to its children.
 *
 * Open whenever one of its screens is the current page, so the menu always
 * shows where you are; otherwise it remembers whether you opened it.
 */
export function NavGroup({ item, pathname }: { item: NavItem; pathname: string }): ReactNode {
  const children = item.children ?? [];
  const containsCurrent = children.some(
    (child) => pathname === child.to || pathname.startsWith(`${child.to}/`),
  );
  const [open, setOpen] = useState(containsCurrent);
  useEffect(() => {
    if (containsCurrent) setOpen(true);
  }, [containsCurrent]);
  const id = `nav-group-${item.label.toLowerCase().replace(/\W+/g, '-')}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
          containsCurrent ? 'text-brand-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
        )}
      >
        <item.icon className="size-4.5 shrink-0" aria-hidden />
        <span className="flex-1">{item.label}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <div id={id} className="mt-0.5 space-y-0.5">
          {children.map((child) => (
            <NavEntry key={child.to} item={child} nested />
          ))}
        </div>
      )}
    </div>
  );
}

/** Page heading used by every module page, for a consistent rhythm. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}


